import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CalendarClock, Plus, Lock, Loader2, AlertTriangle, FileSignature,
  RotateCcw, UserMinus, Trash2, Infinity as InfinityIcon, ShieldAlert,
  CheckCircle2, X,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/utils/api';

const SEM_STYLES = {
  verde: { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500', label: 'Vigente' },
  amarillo: { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500', label: 'Por vencer' },
  rojo: { bg: 'bg-rose-100', text: 'text-rose-800', dot: 'bg-rose-500', label: 'Crítico' },
  neutro: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400', label: 'Resuelto' },
  gris: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-300', label: '—' },
};

const ESTADO_LABEL = {
  vigente: 'Plazo Fijo',
  indefinido: 'Indefinido',
  desvinculado: 'Desvinculado',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-CL', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function VencimientosPlazoFijoTab() {
  const [status, setStatus] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [incluirResueltos, setIncluirResueltos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [renovarFor, setRenovarFor] = useState(null);
  const [desvincularFor, setDesvincularFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, e] = await Promise.all([
        api.get('/vencimientos-pf/status'),
        api.get('/vencimientos-pf/contratos', { params: { incluir_resueltos: incluirResueltos } }),
        api.get('/vencimientos-pf/empleados').catch(() => ({ data: { items: [] } })),
      ]);
      setStatus(s.data);
      setContratos(c.data.items || []);
      setEmpleados(e.data?.items || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        setStatus({ autorizado: false, usuarios_autorizados: err.response.data?.usuarios_autorizados || [] });
      } else {
        toast.error(formatErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, [incluirResueltos]);

  useEffect(() => { load(); }, [load]);

  const rojos = useMemo(
    () => contratos.filter(c => c.semaforo === 'rojo' && c.estado_contrato === 'vigente'),
    [contratos]
  );

  if (loading && !status) {
    return <Card className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></Card>;
  }

  if (status && !status.autorizado) {
    return (
      <Card className="p-12 text-center border-rose-200 bg-rose-50" data-testid="vpf-acceso-denegado">
        <Lock className="w-12 h-12 text-rose-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-rose-900">Acceso restringido</h3>
        <p className="text-sm text-rose-700 mt-2">
          Este submódulo requiere el permiso <strong className="font-mono">rrhh_avanzado</strong> o ser Socio de la firma.<br />
          Solicita acceso a través del Panel de Control.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <CalendarClock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Vencimientos de Contratos a Plazo Fijo</h2>
              <p className="text-xs text-slate-500">
                Alertas automáticas diarias 08:30 AM (10 días y ≤5 días) ·{' '}
                <strong className={status?.gmail_conectado ? 'text-emerald-700' : 'text-amber-700'}>
                  {status?.gmail_conectado ? `Gmail real (${status.email_remitente})` : 'Mock (consola backend)'}
                </strong>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs flex items-center gap-1.5 cursor-pointer text-slate-600">
              <input
                type="checkbox"
                checked={incluirResueltos}
                onChange={(e) => setIncluirResueltos(e.target.checked)}
                data-testid="vpf-toggle-resueltos"
              />
              Incluir indefinidos / desvinculados
            </label>
            <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="vpf-crear-btn">
              <Plus className="w-4 h-4 mr-1" /> Registrar Contrato
            </Button>
          </div>
        </div>
      </Card>

      {/* Banner urgencia rojos */}
      {rojos.length > 0 && (
        <Card className="p-3 border-rose-300 bg-rose-50" data-testid="vpf-banner-rojo">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-rose-900">
              <strong>{rojos.length} contrato(s) en estado crítico (≤5 días):</strong>{' '}
              {rojos.slice(0, 3).map(c => c.empleado_name).join(', ')}
              {rojos.length > 3 ? ` y ${rojos.length - 3} más` : ''}.
              Debe resolverse hoy: Renovar, Pasar a Indefinido o Registrar Desvinculación.
            </div>
          </div>
        </Card>
      )}

      {/* Tabla */}
      <Card className="p-0 border-slate-200 overflow-hidden">
        {contratos.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="vpf-empty">
            <FileSignature className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No hay contratos a plazo fijo registrados.</p>
            <p className="text-xs mt-1">Haz clic en "Registrar Contrato" para empezar.</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2">Funcionario</th>
                <th className="text-left px-3 py-2">Área</th>
                <th className="text-left px-3 py-2">Cargo</th>
                <th className="text-left px-3 py-2">Inicio</th>
                <th className="text-left px-3 py-2">Término</th>
                <th className="text-center px-3 py-2">Iter.</th>
                <th className="text-right px-3 py-2">Días</th>
                <th className="text-center px-3 py-2">Estado contrato</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {contratos.map(c => {
                const sem = SEM_STYLES[c.semaforo] || SEM_STYLES.gris;
                const isVigente = c.estado_contrato === 'vigente';
                return (
                  <tr
                    key={c.id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`vpf-row-${c.id}`}
                  >
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${sem.bg} ${sem.text} text-[10px] font-semibold`}>
                        <span className={`w-2 h-2 rounded-full ${sem.dot}`}></span>
                        {sem.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-800">
                      {c.empleado_name}
                      {c.warning_legal && (
                        <span title={c.warning_legal} className="ml-1 inline-block">
                          <AlertTriangle className="w-3.5 h-3.5 inline text-amber-600" />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{c.empleado_area || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{c.empleado_cargo || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{fmtDate(c.fecha_inicio)}</td>
                    <td className="px-3 py-2 text-slate-800 font-semibold">{fmtDate(c.fecha_termino)}</td>
                    <td className="px-3 py-2 text-center tabular-nums">{c.iteracion}/2</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">
                      {c.dias_restantes === null || c.dias_restantes === undefined ? '—' : `${c.dias_restantes} d`}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">
                        {ESTADO_LABEL[c.estado_contrato] || c.estado_contrato}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isVigente ? (
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => setRenovarFor(c)}
                            className="p-1 text-slate-400 hover:text-indigo-600"
                            data-testid={`vpf-renovar-${c.id}`}
                            title="Renovar contrato"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`¿Pasar a Indefinido el contrato de ${c.empleado_name}?`)) return;
                              try {
                                await api.post(`/vencimientos-pf/contratos/${c.id}/pasar-indefinido`);
                                toast.success('Contrato pasado a Indefinido');
                                load();
                              } catch (err) { toast.error(formatErrorMessage(err)); }
                            }}
                            className="p-1 text-slate-400 hover:text-emerald-600"
                            data-testid={`vpf-indef-${c.id}`}
                            title="Pasar a Indefinido"
                          >
                            <InfinityIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDesvincularFor(c)}
                            className="p-1 text-slate-400 hover:text-rose-600"
                            data-testid={`vpf-desvincular-${c.id}`}
                            title="Registrar Desvinculación"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm('¿Eliminar este registro?')) return;
                              try {
                                await api.delete(`/vencimientos-pf/contratos/${c.id}`);
                                toast.success('Eliminado');
                                load();
                              } catch (err) { toast.error(formatErrorMessage(err)); }
                            }}
                            className="p-1 text-slate-400 hover:text-rose-600"
                            data-testid={`vpf-del-${c.id}`}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-slate-300 inline" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {creating && (
        <CrearContratoDialog
          empleados={empleados}
          onClose={() => setCreating(false)}
          onSuccess={() => { setCreating(false); load(); }}
        />
      )}
      {renovarFor && (
        <RenovarDialog
          contrato={renovarFor}
          onClose={() => setRenovarFor(null)}
          onSuccess={() => { setRenovarFor(null); load(); }}
        />
      )}
      {desvincularFor && (
        <DesvincularDialog
          contrato={desvincularFor}
          onClose={() => setDesvincularFor(null)}
          onSuccess={() => { setDesvincularFor(null); load(); }}
        />
      )}
    </div>
  );
}


// =============================================================
// DIALOG: CREAR
// =============================================================

function CrearContratoDialog({ empleados, onClose, onSuccess }) {
  const [empleado_email, setEmpleadoEmail] = useState('');
  const [fecha_inicio, setFechaInicio] = useState(todayISO());
  const [fecha_termino, setFechaTermino] = useState('');
  const [iteracion, setIteracion] = useState(1);
  const [fecha_inicio_original, setFechaInicioOriginal] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  const diasAcumulados = useMemo(() => {
    if (iteracion !== 2) return null;
    try {
      const inicio = new Date((fecha_inicio_original || fecha_inicio) + 'T00:00:00');
      const fin = new Date(fecha_termino + 'T00:00:00');
      const d = Math.round((fin - inicio) / 86400000);
      return isFinite(d) ? d : null;
    } catch { return null; }
  }, [iteracion, fecha_inicio_original, fecha_inicio, fecha_termino]);

  const warningExceso = iteracion === 2 && diasAcumulados !== null && diasAcumulados > 365;

  const submit = async () => {
    if (!empleado_email || !fecha_inicio || !fecha_termino) {
      toast.error('Empleado, fecha inicio y fecha término son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await api.post('/vencimientos-pf/contratos', {
        empleado_email,
        fecha_inicio,
        fecha_termino,
        iteracion,
        fecha_inicio_original: iteracion === 2 ? (fecha_inicio_original || fecha_inicio) : null,
        notas: notas || null,
      });
      toast.success('Contrato registrado');
      onSuccess();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="vpf-modal-crear">
        <DialogHeader>
          <DialogTitle>Registrar Contrato a Plazo Fijo</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Funcionario *</Label>
            <Select value={empleado_email} onValueChange={setEmpleadoEmail}>
              <SelectTrigger data-testid="vpf-input-empleado"><SelectValue placeholder="Seleccionar funcionario..." /></SelectTrigger>
              <SelectContent>
                {empleados.map(e => (
                  <SelectItem key={e.email} value={e.email}>
                    {e.name} <span className="text-slate-400 text-xs">· {e.area || '—'}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha inicio *</Label>
              <Input type="date" value={fecha_inicio} onChange={(e) => setFechaInicio(e.target.value)} data-testid="vpf-input-finicio" />
            </div>
            <div>
              <Label>Fecha término *</Label>
              <Input type="date" value={fecha_termino} onChange={(e) => setFechaTermino(e.target.value)} data-testid="vpf-input-ftermino" />
            </div>
          </div>
          <div>
            <Label>Iteración del contrato</Label>
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => setIteracion(1)}
                className={`flex-1 px-3 py-2 rounded border text-xs ${iteracion === 1 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-300 text-slate-700'}`}
                data-testid="vpf-iter-1"
              >
                1 — Contrato inicial
              </button>
              <button
                type="button"
                onClick={() => setIteracion(2)}
                className={`flex-1 px-3 py-2 rounded border text-xs ${iteracion === 2 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-slate-300 text-slate-700'}`}
                data-testid="vpf-iter-2"
              >
                2 — Prórroga (último plazo fijo)
              </button>
            </div>
          </div>
          {iteracion === 2 && (
            <div>
              <Label>Fecha inicio original (1er contrato plazo fijo)</Label>
              <Input
                type="date"
                value={fecha_inicio_original}
                onChange={(e) => setFechaInicioOriginal(e.target.value)}
                data-testid="vpf-input-finicio-original"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Si se deja vacío se usará la fecha de inicio actual. Se utiliza para calcular si la suma supera 365 días.
              </p>
            </div>
          )}
          {warningExceso && (
            <div className="p-3 rounded bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Advertencia legal:</strong> La suma de plazos fijos acumulados ({diasAcumulados} días) supera 1 año.
                Conforme al artículo 159 N°4 del Código del Trabajo, una segunda renovación o un total &gt;365 días puede transformar
                el contrato en indefinido. Verifique con asesoría legal antes de confirmar.
              </span>
            </div>
          )}
          <div>
            <Label>Notas (opcional)</Label>
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} data-testid="vpf-input-notas" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="vpf-guardar-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar contrato'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================
// DIALOG: RENOVAR
// =============================================================

function RenovarDialog({ contrato, onClose, onSuccess }) {
  const [nueva_fecha_termino, setNuevaFecha] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  const inicioOriginal = contrato.fecha_inicio_original || contrato.fecha_inicio;
  const diasAcumulados = useMemo(() => {
    if (!nueva_fecha_termino) return null;
    try {
      const i = new Date(inicioOriginal + 'T00:00:00');
      const f = new Date(nueva_fecha_termino + 'T00:00:00');
      return Math.round((f - i) / 86400000);
    } catch { return null; }
  }, [nueva_fecha_termino, inicioOriginal]);
  const warning = diasAcumulados !== null && diasAcumulados > 365;

  const submit = async () => {
    if (!nueva_fecha_termino) {
      toast.error('Ingrese la nueva fecha de término');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/vencimientos-pf/contratos/${contrato.id}/renovar`, {
        nueva_fecha_termino,
        notas: notas || null,
      });
      toast.success('Contrato renovado (iteración 2)');
      onSuccess();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="vpf-modal-renovar">
        <DialogHeader>
          <DialogTitle>Renovar contrato — {contrato.empleado_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 p-2 rounded text-xs text-slate-600">
            Inicio original: <strong>{fmtDate(inicioOriginal)}</strong> · Vence actualmente: <strong>{fmtDate(contrato.fecha_termino)}</strong>
          </div>
          <div>
            <Label>Nueva fecha de término *</Label>
            <Input
              type="date"
              value={nueva_fecha_termino}
              onChange={(e) => setNuevaFecha(e.target.value)}
              min={contrato.fecha_termino}
              data-testid="vpf-renovar-fecha"
            />
          </div>
          {warning && (
            <div className="p-3 rounded bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Tras esta renovación, los plazos acumulados sumarán <strong>{diasAcumulados} días</strong> (&gt;365). Riesgo de transformación a Indefinido.</span>
            </div>
          )}
          <div>
            <Label>Notas (opcional)</Label>
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="vpf-renovar-guardar">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Renovar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================
// DIALOG: DESVINCULAR
// =============================================================

function DesvincularDialog({ contrato, onClose, onSuccess }) {
  const [fecha_efectiva, setFechaEfectiva] = useState(contrato.fecha_termino || todayISO());
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/vencimientos-pf/contratos/${contrato.id}/desvincular`, {
        fecha_efectiva,
        motivo: motivo || null,
      });
      toast.success('Desvinculación registrada');
      onSuccess();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="vpf-modal-desvincular">
        <DialogHeader>
          <DialogTitle>Registrar desvinculación — {contrato.empleado_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Fecha efectiva de término *</Label>
            <Input
              type="date"
              value={fecha_efectiva}
              onChange={(e) => setFechaEfectiva(e.target.value)}
              data-testid="vpf-desv-fecha"
            />
          </div>
          <div>
            <Label>Motivo (opcional)</Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} data-testid="vpf-desv-motivo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="vpf-desv-guardar">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar desvinculación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
